import Editor from '@monaco-editor/react';
import { Box, useTheme } from '@mui/material';
import React from 'react';

interface CodeEditorProps {
  value?: string;
  language?: string;
  onChange?: (code: string) => void;
}

export const CodeEditor: React.FC<CodeEditorProps> = ({
  value = '',
  language = 'python',
  onChange,
}) => {
  const theme = useTheme();
  const editorRef = React.useRef<unknown>(null);

  const handleEditorDidMount = (editor: unknown) => {
    editorRef.current = editor;

    // Configure Monaco editor options
    (
      editor as {
        updateOptions: (options: unknown) => void;
      }
    ).updateOptions({
      fontSize: 14,
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      lineHeight: 20,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      automaticLayout: true,
      tabSize: 4,
      insertSpaces: true,
      wordWrap: 'on',
      lineNumbers: 'on',
      glyphMargin: false,
      folding: false,
      lineDecorationsWidth: 0,
      lineNumbersMinChars: 3,
    });
  };

  const handleEditorChange = (newValue: string | undefined) => {
    if (newValue !== undefined) {
      onChange?.(newValue);
    }
  };

  // Determine Monaco theme based on MUI theme
  const monacoTheme = theme.palette.mode === 'dark' ? 'vs-dark' : 'vs-light';

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Monaco Editor */}
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: theme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        <Editor
          height='100%'
          language={language}
          value={value}
          theme={monacoTheme}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          options={{
            fontSize: 14,
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
            lineHeight: 20,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 4,
            insertSpaces: true,
            wordWrap: 'on',
            lineNumbers: 'on',
            glyphMargin: false,
            folding: false,
            lineDecorationsWidth: 10,
            lineNumbersMinChars: 4,
            padding: { top: 12, bottom: 12, left: 16 },
          }}
          loading={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: theme.palette.text.secondary,
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              }}
            >
              Loading editor...
            </Box>
          }
        />
      </Box>
    </Box>
  );
};
